import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  forwardRef,
} from "react";
import { createPortal } from "react-dom";
import { toneForOption, type ToneSource } from "../../design/enumTone";
import {
  ENUM_SEARCH_THRESHOLD,
  filterEnumOptions,
} from "../../design/enumSearch";
import { CaretDownIcon } from "@phosphor-icons/react";
import {
  computeAnchoredMenuPosition,
  measureMenuContentHeight,
} from "../../anchoredMenu";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";

// Geometry for the portalled option list (#11846, item 19). It opens below
// its button when there is room and above it when there isn't, flipped by
// its own measured height -- the same rules as every other hand-positioned
// RMS menu (anchoredMenu.ts). MENU_MAX_HEIGHT mirrors the `max-h-60` the
// list wears.
const MENU_GAP = 4;
const MENU_EDGE_PADDING = 8;
const MENU_MAX_HEIGHT = 240;
const MENU_MIN_HEIGHT = 120;

interface DropdownGeometry {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
}

interface StatusDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  // Tone annotations from the fact definition (#11426, round 5): the status
  // field's semantic map and/or the tag-category map. Colour derives from
  // the token ramp through these — the org's raw hex never reaches paint.
  toneSource?: ToneSource | null;
  // Optional display labels keyed by option value. When present, the trigger
  // and each option render `enumLabels[option] ?? option` while still emitting
  // the raw option value on change — so an enum can store keys but show
  // labels (same contract as PortalledEnumDropdown). toneSource maps stay
  // keyed by the raw option value.
  enumLabels?: Record<string, string> | null;
  placeholder?: string;
  disabled?: boolean;
  onNavigateUp?: () => void;
  // The trigger renders a <button>, not an input/textarea/select, so the
  // parent's `[&_input]:pl-24` / `[&_select]:pl-24` arbitrary-variant
  // selectors don't reach it. When the parent paints a leading Autofill
  // chip or a trailing Source chip over this input column, it passes
  // these flags through so the button's own padding clears the chips and
  // the selected-value text stays aligned with the other field editors.
  hasAutofillChip?: boolean;
  hasSourceChip?: boolean;
}

const StatusDropdown = forwardRef<HTMLButtonElement, StatusDropdownProps>(
  (
    {
      value,
      onChange,
      options,
      toneSource,
      enumLabels,
      placeholder = "Select an option",
      disabled = false,
      onNavigateUp,
      hasAutofillChip = false,
      hasSourceChip = false,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [searchQuery, setSearchQuery] = useState("");
    const [dropdownPosition, setDropdownPosition] =
      useState<DropdownGeometry | null>(null);
    const internalButtonRef = useRef<HTMLButtonElement>(null);
    const buttonRef =
      (ref as React.RefObject<HTMLButtonElement>) || internalButtonRef;
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
    // Type-ahead: typed characters accumulate briefly and jump the highlight
    // to the first option whose display label starts with the buffer. Only
    // for a list short enough to have no search box; past the threshold the
    // typed characters go into the search box instead (#12128).
    const typeaheadBuffer = useRef("");
    // Clears the accumulated prefix once the user has paused typing for a
    // second (#12187): the debounced-callback shape this hook already
    // provides, rather than a hand-rolled timer ref + unmount cleanup.
    const clearTypeaheadBuffer = useDebouncedCallback(() => {
      typeaheadBuffer.current = "";
    }, 1000);

    const labelFor = (option: string): string => enumLabels?.[option] ?? option;

    // A long list gets a search box (#12128), the same boundary and the same
    // label matching as the scalar cell editor (#10808). Everything below --
    // rendering, keyboard navigation, the option refs used for
    // scroll-into-view -- works off the filtered list, never `options`, so
    // what the arrow keys walk is always what is on screen.
    const showSearch = options.length > ENUM_SEARCH_THRESHOLD;
    const filteredOptions = useMemo(
      () =>
        showSearch
          ? filterEnumOptions(options, searchQuery, enumLabels)
          : options,
      [options, searchQuery, showSearch, enumLabels]
    );

    // Place the list against its button, and keep it there. Two passes, both
    // before paint: the first mounts the list under the button at the
    // button's width so it can be measured at all; the second measures it
    // and picks the side with room. Computing the side in the click handler
    // can't measure anything -- the list isn't in the DOM yet -- which is how
    // this component came to open downward into whatever sliver of window
    // was left below a low field (#11846).
    const isMounted = dropdownPosition !== null;
    useLayoutEffect(() => {
      if (!isOpen) {
        setDropdownPosition(null);
        return;
      }
      const btn = buttonRef.current;
      if (!btn) return;
      const menu = dropdownRef.current;
      if (!menu) {
        const rect = btn.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + MENU_GAP,
          left: rect.left,
          width: rect.width,
          maxHeight: MENU_MAX_HEIGHT,
        });
        return;
      }

      const updatePosition = () => {
        const anchor = btn.getBoundingClientRect();
        const next = computeAnchoredMenuPosition({
          anchor,
          contentHeight: measureMenuContentHeight(menu),
          menuWidth: anchor.width,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
          gap: MENU_GAP,
          edgePadding: MENU_EDGE_PADDING,
          maxHeight: MENU_MAX_HEIGHT,
          minHeight: MENU_MIN_HEIGHT,
        });
        setDropdownPosition((prev) =>
          prev &&
          prev.top === next.top &&
          prev.left === next.left &&
          prev.width === anchor.width &&
          prev.maxHeight === next.maxHeight
            ? prev
            : {
                top: next.top,
                left: next.left,
                width: anchor.width,
                maxHeight: next.maxHeight,
              }
        );
      };

      updatePosition();
      // The option list can change while open (a clear row appears once a
      // value is picked, a search narrows the list), so re-measure on content
      // changes as well as on the button moving. Capture phase, so an inner
      // pane scrolling counts.
      const resizeObserver = new ResizeObserver(updatePosition);
      resizeObserver.observe(menu);
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      return () => {
        resizeObserver.disconnect();
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }, [isOpen, isMounted, buttonRef]);

    // Every open starts with an empty query: a search is a way to find one
    // option, not a filter the list keeps.
    useEffect(() => {
      if (!isOpen) setSearchQuery("");
    }, [isOpen]);

    // The search box takes focus as soon as the list is on screen, so a user
    // who opened the list can type without first clicking into the box.
    useEffect(() => {
      if (isOpen && isMounted && showSearch) {
        searchInputRef.current?.focus();
      }
    }, [isOpen, isMounted, showSearch]);

    // A filtered list renumbers the options, so an index carried over from
    // the previous query would highlight -- and Enter would commit -- a row
    // the user never picked. Every query change, including the one back to
    // empty, puts the highlight on the first visible option; the value that
    // is currently set gets no special treatment mid-search, the same as in
    // PortalledEnumDropdown. This also fires on the open commit, where it
    // would fight the open effect below; it loses that fight on purpose,
    // because React runs effects in declaration order and the open effect's
    // initialization is the one that lands.
    useEffect(() => {
      if (!isOpen) return;
      setHighlightedIndex(0);
    }, [searchQuery, isOpen]);

    // Reset highlight when dropdown opens — unless type-ahead opened it, in
    // which case the highlight it just placed must survive this effect.
    // Reads the visible list (a trigger keystroke opens with a query already
    // set) but re-runs only on open, the options or the value: re-running on
    // every query change would undo the reset above.
    useEffect(() => {
      if (isOpen) {
        if (typeaheadBuffer.current !== "") return;
        const selectedIdx = filteredOptions.findIndex((o) => o === value);
        setHighlightedIndex(selectedIdx >= 0 ? selectedIdx : 0);
      } else {
        setHighlightedIndex(-1);
      }
    }, [isOpen, options, value]);

    // Scroll highlighted option into view
    useEffect(() => {
      if (highlightedIndex >= 0 && optionRefs.current[highlightedIndex]) {
        optionRefs.current[highlightedIndex]?.scrollIntoView({
          block: "nearest",
        });
      }
    }, [highlightedIndex]);

    // Close dropdown when clicking outside
    useEffect(() => {
      if (!isOpen) return;

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        const btn = buttonRef.current;
        if (
          btn &&
          !btn.contains(target) &&
          dropdownRef.current &&
          !dropdownRef.current.contains(target)
        ) {
          setIsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isOpen, buttonRef]);

    const handleOptionClick = (option: string) => {
      onChange(option);
      setIsOpen(false);
    };

    // A printable key on the trigger. With a search box, it opens the list
    // with that character already in the box (focus then moves there, so
    // the rest of the word lands in the box too); without one, it is the
    // prefix type-ahead: the first option whose display label starts with
    // the typed prefix (case-insensitive) is highlighted, and the buffer
    // clears after a pause. A leading space keeps its open/select behavior
    // below; once the buffer is non-empty a space is part of the label being
    // typed ("capital c").
    const handleTypeahead = (e: React.KeyboardEvent): boolean => {
      if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) {
        return false;
      }
      if (showSearch) {
        if (isOpen || e.key === " ") return false;
        setSearchQuery(e.key);
        setIsOpen(true);
        return true;
      }
      if (e.key === " " && typeaheadBuffer.current === "") return false;
      typeaheadBuffer.current += e.key.toLowerCase();
      clearTypeaheadBuffer();
      const idx = options.findIndex((option) =>
        labelFor(option).toLowerCase().startsWith(typeaheadBuffer.current)
      );
      if (!isOpen) setIsOpen(true);
      if (idx >= 0) setHighlightedIndex(idx);
      return true;
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (disabled) return;

      if (handleTypeahead(e)) {
        e.preventDefault();
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else {
            setHighlightedIndex((prev) =>
              Math.min(prev + 1, filteredOptions.length - 1)
            );
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          if (isOpen) {
            setHighlightedIndex((prev) => Math.max(prev - 1, 0));
          } else {
            onNavigateUp?.();
          }
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          if (
            isOpen &&
            highlightedIndex >= 0 &&
            highlightedIndex < filteredOptions.length
          ) {
            handleOptionClick(filteredOptions[highlightedIndex]);
          } else if (!isOpen) {
            setIsOpen(true);
          }
          break;
        case "Escape":
          if (isOpen) {
            e.preventDefault();
            e.nativeEvent.stopPropagation();
            setIsOpen(false);
          }
          break;
      }
    };

    // Keys in the search box. Printable characters are the query and fall
    // through to the input; these four drive the list the way the trigger's
    // handler does, so the user never has to leave the box to pick.
    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            Math.min(prev + 1, filteredOptions.length - 1)
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case "Enter":
          e.preventDefault();
          if (
            highlightedIndex >= 0 &&
            highlightedIndex < filteredOptions.length
          ) {
            handleOptionClick(filteredOptions[highlightedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          e.nativeEvent.stopPropagation();
          setIsOpen(false);
          buttonRef.current?.focus();
          break;
      }
    };

    const selectedColorClasses = value
      ? toneForOption(value, toneSource)
      : null;

    return (
      <div className="relative">
        {/* Trigger Button */}
        <button
          ref={buttonRef}
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className={`w-full py-2 text-left text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--fp-ink-3)] focus:border-[var(--fp-ink-3)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between ${
            hasAutofillChip ? "pl-24" : "pl-3"
          } ${hasSourceChip ? "pr-24" : "pr-3"} ${
            selectedColorClasses ? "font-medium" : "bg-white"
          }`}
          style={
            selectedColorClasses
              ? {
                  backgroundColor: selectedColorClasses.backgroundColor,
                  color: selectedColorClasses.textColor,
                  borderColor: selectedColorClasses.borderColor,
                }
              : undefined
          }
        >
          <span
            className={`truncate ${!value && !selectedColorClasses ? "text-[var(--fp-ink-4)]" : ""}`}
          >
            {value ? labelFor(value) : placeholder}
          </span>
          <CaretDownIcon
            size={16}
            className={`ml-2 flex-shrink-0 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {/* Portalled Popover Dropdown */}
        {isOpen &&
          dropdownPosition &&
          createPortal(
            <div
              ref={dropdownRef}
              className="fixed bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto portalled-enum-dropdown"
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                maxHeight: dropdownPosition.maxHeight,
                zIndex: 9999,
              }}
            >
              {showSearch && (
                <div className="sticky top-0 z-10 bg-white p-2 border-b border-gray-200">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Search options..."
                    className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-[var(--fp-ink-3)]"
                  />
                </div>
              )}
              {value && (
                // Clearing affordance — the native <select> this component
                // replaced relied on a blank placeholder option for the same
                // purpose. Mirror that behavior by reusing the placeholder
                // string so the row reads identically to how the trigger
                // will read once cleared.
                <button
                  type="button"
                  onClick={() => handleOptionClick("")}
                  className="w-full px-3 py-2 text-left text-sm text-gray-500 italic focus:outline-none hover:bg-gray-50 border-b border-gray-100"
                >
                  {placeholder}
                </button>
              )}
              {/* Only when a query actually excluded something: an empty
                  option list with no query is a legitimately empty enum, not
                  a search that found nothing. */}
              {filteredOptions.length === 0 && searchQuery.trim() !== "" && (
                <div className="px-3 py-2 text-sm text-gray-500">
                  No matching options
                </div>
              )}
              {filteredOptions.map((option, index) => {
                const optionColorClasses = toneForOption(option, toneSource);
                const isHighlighted = index === highlightedIndex;
                return (
                  <button
                    key={option}
                    ref={(el) => {
                      optionRefs.current[index] = el;
                    }}
                    type="button"
                    onClick={() => handleOptionClick(option)}
                    className={`w-full px-3 py-2 text-left text-sm focus:outline-none flex items-center justify-between border-b border-gray-100 last:border-b-0 ${
                      optionColorClasses ? "font-medium" : ""
                    } ${value === option ? "ring-2 ring-[var(--fp-ink-2)] ring-inset" : ""} ${
                      isHighlighted && !optionColorClasses
                        ? "bg-gray-100"
                        : !optionColorClasses
                          ? "hover:bg-gray-50"
                          : ""
                    }`}
                    style={
                      optionColorClasses
                        ? {
                            backgroundColor: isHighlighted
                              ? optionColorClasses.borderColor
                              : optionColorClasses.backgroundColor,
                            color: optionColorClasses.textColor,
                          }
                        : undefined
                    }
                  >
                    <span className="truncate">{labelFor(option)}</span>
                    {value === option && (
                      <svg
                        className="w-4 h-4 ml-2 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>,
            document.body
          )}
      </div>
    );
  }
);

StatusDropdown.displayName = "StatusDropdown";

export default StatusDropdown;
